import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "About",
  "includeToc": false,
  "tags": ["About", "DOI", "BLM", "OSMRE", "BOEM", "BSEE", "ONRR", "Treasury", "IRS"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Box = makeShortcode("Box");
const Container = makeShortcode("Container");
const Grid = makeShortcode("Grid");
const GlossaryTerm = makeShortcode("GlossaryTerm");
const Link = makeShortcode("Link");
const DoiLogoImg = makeShortcode("DoiLogoImg");
const BlmLogoImg = makeShortcode("BlmLogoImg");
const OsmreLogoImg = makeShortcode("OsmreLogoImg");
const BoemLogoImg = makeShortcode("BoemLogoImg");
const BseeLogoImg = makeShortcode("BseeLogoImg");
const OnrrLogoImg = makeShortcode("OnrrLogoImg");
const DotLogoImg = makeShortcode("DotLogoImg");
const IrsLogoImg = makeShortcode("IrsLogoImg");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <Box bgcolor='primary.main' mdxType="Box">
      <Container maxWidth="lg" mdxType="Container">
        <Grid container spacing={2} alignItems='center' mdxType="Grid">
          <Grid item xs={12} sm={8} mdxType="Grid">
            <Box p={3} pl={0} mdxType="Box">
              <h1 {...{
                "id": "About-This-Site",
                "style": {
                  "position": "relative"
                }
              }}>{`About This Site`}</h1>
              <p>{`The United States is a major developer of natural resources. The Department of the Interior (DOI) collects billions of dollars in annual revenue
from companies that lease `}<GlossaryTerm mdxType="GlossaryTerm">{`federal lands and waters`}</GlossaryTerm>{` in order to develop oil, gas, or mineral
resources. These revenues are `}<Link href="/how-revenue-works#understanding-federal-disbursements" mdxType="Link">{`disbursed`}</Link>{` to the U.S. Treasury, other federal agencies, states,
Native American tribes, and individual Native American mineral owners.`}</p>
              <p>{`This site provides data and contextual information about how natural resources and their revenues are managed in the U.S.`}</p>
            </Box>
          </Grid>
          <Grid container xs={12} sm={4} mdxType="Grid">
            <Box p={3} border={4} borderTop={0} borderBottom={0} borderColor='white' mdxType="Box">
              <Grid item xs={12} mdxType="Grid">
                <p><strong parentName="p">{`Understand natural resource management on federal land`}</strong></p>
                <ul>
                  <li parentName="ul"><a parentName="li" {...{
                      "href": "/sites/VDP/how-revenue-works/ownership/"
                    }}>{`Land ownership`}</a></li>
                  <li parentName="ul"><a parentName="li" {...{
                      "href": "/sites/VDP/how-revenue-works/#what-laws-regulations-govern-natural-resource-extraction-in-the-us"
                    }}>{`Laws and regulations`}</a></li>
                  <li parentName="ul"><a parentName="li" {...{
                      "href": "/sites/VDP/how-revenue-works/#the-production-process"
                    }}>{`Oil, gas, minerals, and renewable energy`}</a></li>
                  <li parentName="ul"><a parentName="li" {...{
                      "href": "/sites/VDP/how-revenue-works/audits-and-assurances/"
                    }}>{`Audits and assurances`}</a></li>
                </ul>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
    {
      /* Begin content */
    }
    <Container maxWidth="lg" component="section" mdxType="Container">
      <h2 {...{
        "id": "Whos-involved",
        "style": {
          "position": "relative"
        }
      }}>{`Who's involved`}</h2>
      <hr></hr>
      <p>{`Congress passes laws to govern the extraction of natural resources and the fiscal management of resulting revenue.
Federal agencies develop regulations and rules to implement and enforce those laws. DOI has primary responsibility for
implementing the relevant statutes and regulations in consultation with other federal agencies.`}</p>
      <Box mt={5} mb={5} mdxType="Box">
        <Grid container spacing={3} mdxType="Grid">
          <Grid item xs={6} sm={2} mdxType="Grid">
            <DoiLogoImg mdxType="DoiLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Department-of-the-Interior-DOI",
              "style": {
                "position": "relative"
              }
            }}>{`Department of the Interior (DOI)`}</h3>
            <p><a parentName="p" {...{
                "href": "https://www.doi.gov/"
              }}>{`DOI`}</a>{` protects and manages the nation’s natural resources and cultural heritage; provides scientific and other information
about those resources; and honors its trust responsibilities or special commitments to Native Americans, Alaska Natives, and affiliated island communities.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <BlmLogoImg mdxType="BlmLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Bureau-of-Land-Management-BLM",
              "style": {
                "position": "relative"
              }
            }}>{`Bureau of Land Management (BLM)`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.blm.gov/"
              }}>{`BLM`}</a>{` manages exploration, development, and production of natural resources on federal lands, including lease sales and the permitting and licensing
processes. BLM also ensures that developers and operators comply with requirements and regulations. BLM collects revenue in the form of `}<GlossaryTerm mdxType="GlossaryTerm">{`bonus`}</GlossaryTerm>{` bids,
first year rentals, and fees.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <OsmreLogoImg mdxType="OsmreLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Office-of-Surface-Mining-Reclamation-and-Enforcement-OSMRE",
              "style": {
                "position": "relative"
              }
            }}>{`Office of Surface Mining Reclamation and Enforcement (OSMRE)`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.osmre.gov/"
              }}>{`OSMRE`}</a>{` implements requirements of the Surface Mining Control and `}<GlossaryTerm mdxType="GlossaryTerm">{`Reclamation`}</GlossaryTerm>{` Act by working with states and tribes to ensure
that citizens and the environment are protected during coal mining and that land is restored to beneficial use when mining is finished.
OSMRE and its partners are also responsible for the `}<a parentName="p" {...{
                "href": "https://revenuedata.doi.gov/how-revenue-works/aml-reclamation-program/"
              }}>{`Abandoned Mine Land reclamation program`}</a>{`,
which aims to reclaim and restore lands and waters degraded by mining operations before 1977.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <BoemLogoImg mdxType="BoemLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Bureau-of-Ocean-Energy-Management-BOEM",
              "style": {
                "position": "relative"
              }
            }}>{`Bureau of Ocean Energy Management (BOEM)`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.boem.gov/"
              }}>{`BOEM`}</a>{` manages responsible ocean energy development in federal submerged lands, including leasing, plan administration,
environmental analysis, resource evaluation, economic analysis, and the renewable energy program. BOEM also updates
`}<a parentName="p" {...{
                "href": "http://www.boem.gov/National-Environmental-Policy-Act/"
              }}>{`leasing regulations for the Outer Continental Shelf`}</a>{`.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <BseeLogoImg mdxType="BseeLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Bureau-of-Safety-and-Environmental-Enforcement-BSEE",
              "style": {
                "position": "relative"
              }
            }}>{`Bureau of Safety and Environmental Enforcement (BSEE)`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.bsee.gov/"
              }}>{`BSEE`}</a>{` is responsible for safety oversight of ocean energy development and production, including permitting and inspections,
regulatory programs, and oil spill response. BSEE also updates rules governing operations on the `}<GlossaryTerm mdxType="GlossaryTerm">{`Outer Continental Shelf`}</GlossaryTerm>{`.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
  <OnrrLogoImg style={{
              height: '100%',
              width: '100%'
            }} mdxType="OnrrLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Office-of-Natural-Resources-Revenue-ONRR",
              "style": {
                "position": "relative"
              }
            }}>{`Office of Natural Resources Revenue (ONRR)`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.onrr.gov/"
              }}>{`ONRR`}</a>{` collects, accounts for, and verifies revenues from natural resource extraction on federal and Native American land
for the benefit of all Americans. ONRR collects revenue from energy and mineral leases for both onshore and offshore federal and
Native American lands and disburses revenues to states, Native Americans, and the U.S. Treasury.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <DotLogoImg mdxType="DotLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Department-of-the-Treasury",
              "style": {
                "position": "relative"
              }
            }}>{`Department of the Treasury`}</h3>
            <p><a parentName="p" {...{
                "href": "http://www.treasury.gov/Pages/default.aspx"
              }}>{`The Treasury`}</a>{` supports economic growth and stability in the U.S. and overseas, protects the U.S. financial system, and
manages the federal government’s finances and resources.`}</p>
          </Grid>
          <Grid item xs={12} mdxType="Grid">
            {
              /* Used for spacing */
            }
          </Grid>
          <Grid item xs={6} sm={2} mdxType="Grid">
            <IrsLogoImg mdxType="IrsLogoImg" />
          </Grid>
          <Grid item xs={12} sm={10} mdxType="Grid">
            <h3 {...{
              "id": "Internal-Revenue-Service-IRS",
              "style": {
                "position": "relative"
              }
            }}>{`Internal Revenue Service (IRS)`}</h3>
            <p><a parentName="p" {...{
                "href": "https://www.irs.gov/"
              }}>{`The IRS`}</a>{` collects corporate income taxes from corporations in the extractive industries, as well as income taxes from
all other companies operating in these industries.`}</p>
          </Grid>
        </Grid>
        <h2 {...{
          "id": "History",
          "style": {
            "position": "relative"
          }
        }}>{`History`}</h2>
        <p>{`This site was originally built to support the United States' participation in the `}<a parentName="p" {...{
            "href": "https://eiti.org/"
          }}>{`Extractive Industries Transparency Initiative (EITI)`}</a>{`,
which the U.S. joined in 2011.`}</p>
        <p>{`In November 2017, the U.S. decided to no longer formally implement the `}<GlossaryTerm mdxType="GlossaryTerm">{`EITI Standard`}</GlossaryTerm>{`, but remains a strong supporter of
good governance and the principles of transparency represented by the EITI.`}</p>
        <p><a parentName="p" {...{
            "href": "https://archive.revenuedata.doi.gov/"
          }}>{`Learn more about the history of this site and review the archive.`}</a></p>
      </Box>
    </Container>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      